import { useMemo, useState } from "react";
import { Search } from "lucide-react";
import Button from "../components/Button";
import DrawdownMeter from "../components/DrawdownMeter";
import InlineSelect from "../components/InlineSelect";
import Modal from "../components/Modal";
import Pagination from "../components/Pagination";
import SectionHeader from "../components/SectionHeader";
import StatCard, { StatGrid } from "../components/StatCard";
import Tag from "../components/Tag";
import TextLink from "../components/TextLink";
import controlStyles from "../components/ListControls.module.css";
import tableStyles from "../components/DataTable.module.css";
import downloadCsv from "../downloadCsv";
import { NO_DATA, formatNumber, formatPercent, formatSignedPercent, formatUsd } from "../format";
import useApiQuery from "../useApiQuery";
import { RISK_ALERTS, RISK_PERIODS, RISK_STATUS } from "./riskData";
import styles from "./RiskDeskPage.module.css";

const PAGE_SIZE = 10;

const ACCOUNT_COLUMNS = [
  { label: "MT5 acc no.", width: 132 },
  { label: "Email", width: 220 },
  { label: "Trader equity", width: 150 },
  { label: "Copy %", width: 96 },
  { label: "Daily DD", width: 170 },
  { label: "Max DD", width: 170 },
  { label: "Status", width: 120, align: "end" },
];

// How each modal column reads an account from the API
const MODAL_COLUMNS = {
  login: { label: "MT5 acc no.", render: (a) => a.login ?? NO_DATA },
  email: { label: "Email", render: (a) => a.email },
  dailyDd: { label: "Daily DD", align: "end", strong: true, render: (a) => `-${formatPercent(a.dailyDd, 2)}` },
  maxDd: { label: "Max DD", align: "end", strong: true, render: (a) => `-${formatPercent(a.maxDd, 2)}` },
  equity: {
    label: "Equity",
    align: "end",
    strong: true,
    render: (a) => `${formatUsd(a.equity, 2)} (-${formatPercent(a.maxDd)})`,
  },
  status: {
    label: "Status",
    align: "center",
    render: (a) => <Tag tone={RISK_STATUS[a.status].tone}>{RISK_STATUS[a.status].label}</Tag>,
  },
  profitPct: { label: "PNL %", align: "end", positive: true, render: (a) => formatSignedPercent(a.profitPct, 1) },
};

const DRAWDOWN_BANDS = {
  band5: { label: ">-5% max DD", title: "Accounts past -5% max drawdown" },
  band10: { label: "-10% to 20% max DD", title: "Accounts between -10% and -20% max drawdown" },
};

const alignClass = (align) => (align === "end" ? styles.alignEnd : align === "center" ? styles.alignCenter : "");

function exportReport(accounts) {
  downloadCsv(
    "risk-desk.csv",
    ["MT5 acc no.", "Trader", "Email", "Equity", "Profit %", "Daily DD %", "Max DD %", "Status"],
    accounts.map((a) => [
      a.login ?? "",
      a.trader,
      a.email,
      a.equity ?? "",
      a.profitPct ?? "",
      a.dailyDd ?? "",
      a.maxDd ?? "",
      RISK_STATUS[a.status].label,
    ])
  );
}

// `list` is { title, columns: [key], accounts }
function AccountsModal({ list, onClose }) {
  const columns = list ? list.columns.map((key) => ({ key, ...MODAL_COLUMNS[key] })) : [];

  return (
    <Modal open={Boolean(list)} title={list?.title ?? ""} onClose={onClose}>
      {list ? (
        <div className={styles.modalTable}>
          <table className={`${tableStyles.table} ${styles.alertTable}`}>
            <thead>
              <tr>
                {columns.map((column) => (
                  <th key={column.key} scope="col" className={alignClass(column.align)}>
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {list.accounts.map((account) => (
                <tr key={account.id}>
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className={`${alignClass(column.align)} ${
                        column.positive ? styles.positive : column.strong ? styles.strong : ""
                      }`}
                    >
                      {column.render(account)}
                    </td>
                  ))}
                </tr>
              ))}
              {list.accounts.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className={controlStyles.empty}>
                    No accounts right now.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      ) : null}
    </Modal>
  );
}

function NotTracked({ children }) {
  return <p className={`${styles.notTracked} ${controlStyles.empty}`}>{children}</p>;
}

export default function RiskDeskPage() {
  const [period, setPeriod] = useState("week");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [openList, setOpenList] = useState(null);
  const { data, status, reload } = useApiQuery("/api/admin/risk", { period });

  const accounts = useMemo(() => {
    const all = data?.accounts ?? [];
    const normalized = query.trim().toLowerCase();
    return normalized
      ? all.filter((a) => [a.login, a.email, a.trader].some((v) => v && String(v).toLowerCase().includes(normalized)))
      : all;
  }, [data, query]);

  const pageCount = Math.max(1, Math.ceil(accounts.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const visible = accounts.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const header = (
    <header className={`${styles.pageHeader} d-flex align-items-start justify-content-between`}>
      <div className={styles.heading}>
        <h1 className={styles.title}>Risk desk</h1>
        <p className={styles.subtitle}>
          Monitor live trader risk, market exposure, hedge activity and concentration across funded accounts.
        </p>
      </div>
      <Button variant="outline" disabled={!data} onClick={() => exportReport(data.accounts)}>
        Export report
      </Button>
    </header>
  );

  if (!data) {
    return (
      <section className={styles.page}>
        {header}
        {status === "error" ? (
          <NotTracked>
            We couldn't load the risk desk.{" "}
            <TextLink arrow={false} onClick={reload}>
              Try again
            </TextLink>
          </NotTracked>
        ) : (
          <NotTracked>Loading the risk desk…</NotTracked>
        )}
      </section>
    );
  }

  const { overview, alerts, portfolio } = data;
  const view = (list) => (list.accounts.length ? <TextLink onClick={() => setOpenList(list)}>View</TextLink> : null);
  const accountList = (title, columns, list) => ({ title, columns, accounts: list ?? [] });

  const overviewCards = [
    { label: "Tracked accounts", value: formatNumber(overview.trackedAccounts) },
    { label: "Active accounts", value: formatNumber(overview.activeAccounts) },
    {
      label: "Accounts on risk watch",
      value: formatNumber(overview.onRiskWatch),
      suffix: overview.onRiskWatchPct === null ? null : `${formatPercent(overview.onRiskWatchPct)} of active accounts`,
    },
    { label: "Daily loss breaches", value: formatNumber(overview.dailyLossBreaches) },
    { label: "Max loss breaches", value: formatNumber(overview.maxLossBreaches) },
    { label: "Payout eligible accounts", value: formatNumber(overview.payoutEligible) },
  ];

  const portfolioCards = [
    {
      label: "Accounts in profit",
      value: formatNumber(portfolio.inProfit),
      suffix: portfolio.inProfitPct === null ? null : formatPercent(portfolio.inProfitPct),
    },
    {
      label: "Accounts in loss",
      value: formatNumber(portfolio.inLoss),
      suffix: portfolio.inLossPct === null ? null : formatPercent(portfolio.inLossPct),
    },
    {
      label: "Breached daily loss",
      value: formatNumber(portfolio.breachedDailyLoss.length),
      action: view(accountList("Breached daily loss", ["login", "email", "dailyDd"], portfolio.breachedDailyLoss)),
    },
    {
      label: "Breached max loss",
      value: formatNumber(portfolio.breachedMaxLoss.length),
      action: view(accountList("Breached max loss", ["login", "email", "equity"], portfolio.breachedMaxLoss)),
    },
    ...Object.entries(DRAWDOWN_BANDS).map(([key, band]) => ({
      label: band.label,
      value: formatNumber(portfolio[key].length),
      action: view(accountList(band.title, ["login", "email", "maxDd"], portfolio[key])),
    })),
  ];

  return (
    <section className={styles.page}>
      {header}

      <section>
        <SectionHeader title="Overview" />
        <StatGrid>
          {overviewCards.map((stat) => (
            <StatCard key={stat.label} {...stat} />
          ))}
        </StatGrid>
      </section>

      <section>
        <SectionHeader title="Action required" />
        <ul className={styles.alerts}>
          {RISK_ALERTS.map((alert) => {
            const list = alerts[alert.key];

            return (
              <li key={alert.key} className={`${styles.alertRow} d-flex align-items-center justify-content-between`}>
                <p className="d-flex align-items-center flex-wrap gap-2">
                  {alert.summary(list?.length ?? 0)}
                  {list === null ? (
                    <Tag tone="neutral">Not tracked yet</Tag>
                  ) : alert.crossCheck ? (
                    <Tag tone="neutral">Cross check disabling</Tag>
                  ) : null}
                </p>
                {list?.length ? (
                  <TextLink onClick={() => setOpenList(accountList(alert.title, alert.columns, list))}>View all</TextLink>
                ) : null}
              </li>
            );
          })}
        </ul>
      </section>

      <section>
        <SectionHeader
          title="Portfolio risk"
          aside={
            <div className={styles.period}>
              <InlineSelect label="Period" options={RISK_PERIODS} value={period} onChange={setPeriod} />
            </div>
          }
        />
        <StatGrid>
          {portfolioCards.map((card) => (
            <StatCard key={card.label} {...card} />
          ))}
        </StatGrid>
      </section>

      <section>
        <SectionHeader title="Positions per instrument" />
        {/* Open positions aren't collected from MT5 yet; the API sends null until they are */}
        <NotTracked>Positions per instrument aren't tracked yet.</NotTracked>
      </section>

      <section>
        <SectionHeader title="PMM cockpit" />
        <NotTracked>The PMM cockpit appears once funded accounts are hedged through the PMM.</NotTracked>

        <div className={`${controlStyles.toolbar} ${styles.searchRow} d-flex justify-content-end`}>
          <label className={`${controlStyles.search} d-flex align-items-center`}>
            <Search size={20} strokeWidth={1.75} aria-hidden="true" />
            <input
              type="search"
              placeholder="Search account or email"
              aria-label="Search account or email"
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setPage(1);
              }}
            />
          </label>
        </div>

        <div className={tableStyles.wrap}>
          <table className={`${tableStyles.table} ${tableStyles.fixed} ${styles.accountsTable}`}>
            <colgroup>
              {ACCOUNT_COLUMNS.map((column) => (
                <col key={column.label} style={{ width: column.width }} />
              ))}
            </colgroup>
            <thead>
              <tr>
                {ACCOUNT_COLUMNS.map((column) => (
                  <th key={column.label} scope="col" className={alignClass(column.align)}>
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map((account) => (
                <tr key={account.id}>
                  <td>{account.login ?? NO_DATA}</td>
                  <td>{account.email}</td>
                  <td>{formatUsd(account.equity)}</td>
                  <td>{account.copyPct === null ? NO_DATA : `${account.copyPct}%`}</td>
                  <td>
                    <DrawdownMeter value={account.dailyDd} limit={data.rules.dailyLossPct} label="Daily drawdown" />
                  </td>
                  <td>
                    <DrawdownMeter value={account.maxDd} limit={data.rules.maxDrawdownPct} label="Max drawdown" />
                  </td>
                  <td className={styles.alignEnd}>
                    <Tag tone={RISK_STATUS[account.status].tone}>{RISK_STATUS[account.status].label}</Tag>
                  </td>
                </tr>
              ))}
              {visible.length === 0 ? (
                <tr>
                  <td colSpan={ACCOUNT_COLUMNS.length} className={controlStyles.empty}>
                    {query ? "No accounts match this search." : "No live accounts right now."}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        <Pagination page={currentPage} pageCount={pageCount} onChange={setPage} />
      </section>

      <AccountsModal list={openList} onClose={() => setOpenList(null)} />
    </section>
  );
}
